import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { GeoJSON, useMapEvents } from 'react-leaflet';
import { stoneClassOf, stoneSeenLabel } from '../../fields.utils';

const SELECTED_COLOR = '#2154E8';
const LEFT_OUT_RADIUS = 5;
const LEFT_OUT_COLOR = '#3C4644';
const LEFT_OUT_OPACITY = 0.32;

const markerStyle = (stone, isSelected, isListed) => {
  const stoneClass = stoneClassOf(stone.size_class);
  const radius = isListed ? stoneClass.radius : LEFT_OUT_RADIUS;
  return {
    radius: isSelected ? radius + 2 : radius,
    fillColor: isListed ? stoneClass.color : LEFT_OUT_COLOR,
    fillOpacity: isListed ? 1 : LEFT_OUT_OPACITY,
    color: isSelected ? SELECTED_COLOR : 'rgba(255, 255, 255, 0.9)',
    weight: isSelected ? 2.6 : isListed ? 1.1 : 0,
    bubblingMouseEvents: false
  };
};

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
};

const tooltipOf = stone => {
  const root = document.createElement('div');
  const title = element(
    'b',
    'stone-tip-title',
    stone.size_cm == null ? 'Stone, size not known' : `${stone.size_cm} cm stone`
  );
  root.appendChild(title);
  if (stone.size_cm != null) {
    root.appendChild(
      element(
        'span',
        'stone-tip-est',
        stone.size_is_mocked ? ' mocked size' : ' estimated'
      )
    );
  }
  root.appendChild(element('div', 'stone-tip-row', `Stone #${stone.id}`));
  const seen = stoneSeenLabel(stone);
  if (seen) {
    root.appendChild(element('div', 'stone-tip-row', `Seen on ${seen}`));
  }
  return root;
};

const layerKey = features =>
  `stones-${features.length}-${features.reduce(
    (sum, { properties }) => sum + properties.id,
    0
  )}`;

export const StoneLayer = ({ stones, selectedId, listedIds, onSelect }) => {
  const group = useRef();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useMapEvents({ click: () => onSelectRef.current(null) });

  useEffect(() => {
    group.current?.eachLayer(layer => {
      const { id } = layer.feature.properties;
      const isSelected = id === selectedId;
      const isListed = !listedIds || listedIds.has(id);
      layer.setStyle(markerStyle(layer.feature.properties, isSelected, isListed));
      if (!isListed) layer.bringToBack();
      if (isSelected) layer.bringToFront();
    });
  }, [selectedId, listedIds, stones]);

  return (
    <GeoJSON
      ref={group}
      key={layerKey(stones.features)}
      data={stones}
      pointToLayer={(feature, latlng) =>
        L.circleMarker(
          latlng,
          markerStyle(
            feature.properties,
            feature.properties.id === selectedId,
            !listedIds || listedIds.has(feature.properties.id)
          )
        )
      }
      onEachFeature={(feature, layer) => {
        layer.bindTooltip(tooltipOf(feature.properties), {
          sticky: true,
          direction: 'auto',
          offset: [14, -18],
          className: 'stone-tip'
        });
        layer.on('click', () => onSelectRef.current(feature.properties.id));
      }}
    />
  );
};
